import { FC, useEffect, useState, type ReactElement } from 'react';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { Add, Remove, Download } from '@mui/icons-material';
import { usePDF } from '@react-pdf/renderer';
import { Document, Page, pdfjs } from 'react-pdf';
import { toDocumentFileName, triggerBrowserDownload } from '../../../utils/documentFileName';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Must match react-pdf's bundled pdfjs-dist (see react-pdf/package.json)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.15;

const toolBtnSx = {
  width: 46,
  height: 46,
  color: 'common.white',
  bgcolor: 'secondary.main',
  border: '1px solid',
  borderColor: 'secondary.main',
  '&:hover': {
    color: 'common.white',
    bgcolor: 'secondary.main',
    borderColor: 'secondary.main',
  },
  '&.Mui-disabled': {
    color: 'common.white',
    bgcolor: 'secondary.light',
    borderColor: 'secondary.light',
  },
} as const;

interface PdfPreviewWithToolsProps {
  document: ReactElement;
  fileName: string;
  enabled?: boolean;
}

/**
 * Factsheet preview with PDF-native zoom (pdf.js) + named download.
 * Zoom scales the PDF pages themselves — same idea as Ctrl+scroll in Chrome's viewer.
 */
export const PdfPreviewWithTools: FC<PdfPreviewWithToolsProps> = ({
  document,
  fileName,
  enabled = true,
}) => {
  const [instance, updateInstance] = usePDF();
  const [zoom, setZoom] = useState(1);
  const [numPages, setNumPages] = useState(0);

  useEffect(() => {
    if (enabled && document) {
      updateInstance(document);
    }
  }, [document, enabled]);

  const file = instance.url || null;

  const handleDownload = () => {
    if (!instance.blob) return;
    triggerBrowserDownload(instance.blob, toDocumentFileName(fileName));
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        bgcolor: '#525659',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 2,
        }}
      >
        {file ? (
          <Document
            file={file}
            loading={
              <Box sx={{ color: '#fff', p: 3, fontSize: 14 }}>Preparing preview…</Box>
            }
            error={
              <Box sx={{ color: '#fff', p: 3, fontSize: 14 }}>Failed to render PDF</Box>
            }
            onLoadSuccess={({ numPages: next }) => setNumPages(next)}
          >
            <Stack spacing={2} alignItems="center">
              {Array.from({ length: numPages }, (_, i) => (
                <Box
                  key={`page-${i + 1}`}
                  sx={{
                    boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
                    bgcolor: '#fff',
                  }}
                >
                  <Page
                    pageNumber={i + 1}
                    scale={zoom}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Box>
              ))}
            </Stack>
          </Document>
        ) : (
          <Box sx={{ color: '#fff', p: 3, fontSize: 14 }}>
            {instance.error ? 'Failed to render PDF' : 'Preparing preview…'}
          </Box>
        )}
      </Box>

      <Stack
        spacing={5}
        sx={{
          position: 'absolute',
          right: 36,
          bottom: 28,
          zIndex: 2,
        }}
      >
        <Tooltip title="Zoom in" placement="left">
          <span>
            <IconButton
              size="medium"
              onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))}
              disabled={zoom >= MAX_ZOOM || !file}
              sx={toolBtnSx}
              aria-label="Zoom in"
            >
              <Add />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Zoom out" placement="left">
          <span>
            <IconButton
              size="medium"
              onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))}
              disabled={zoom <= MIN_ZOOM || !file}
              sx={toolBtnSx}
              aria-label="Zoom out"
            >
              <Remove />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={`Download ${fileName}`} placement="left">
          <span>
            <IconButton
              size="medium"
              onClick={handleDownload}
              disabled={!instance.blob}
              sx={toolBtnSx}
              aria-label="Download PDF"
            >
              <Download />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};
